<script setup>
import { computed, reactive } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useVuelidate } from '@vuelidate/core';
import { helpers, required } from '@vuelidate/validators';

import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import PageHeader from '../../SettingsSubPageHeader.vue';

const store = useStore();
const router = useRouter();
const { t } = useI18n();

const form = reactive({
  channelName: '',
  robotId: '',
  webhookUrl: '',
});

// WorkTool 收件箱需要明确标记机器人，供后续入站消息映射使用。
const isWebhookUrl = helpers.withMessage(
  () => t('INBOX_MGMT.ADD.WORKTOOL_CHANNEL.WEBHOOK_URL.ERROR'),
  value => /^https?:\/\//i.test(value || '')
);
const requiredChannelName = helpers.withMessage(
  () => t('INBOX_MGMT.ADD.WORKTOOL_CHANNEL.CHANNEL_NAME.ERROR'),
  required
);
const requiredRobotId = helpers.withMessage(
  () => t('INBOX_MGMT.ADD.WORKTOOL_CHANNEL.ROBOT_ID.ERROR'),
  required
);
const requiredWebhookUrl = helpers.withMessage(
  () => t('INBOX_MGMT.ADD.WORKTOOL_CHANNEL.WEBHOOK_URL.REQUIRED'),
  required
);

const rules = {
  channelName: { requiredChannelName },
  robotId: { requiredRobotId },
  webhookUrl: { requiredWebhookUrl, isWebhookUrl },
};

const v$ = useVuelidate(rules, form);
const isCreating = computed(
  () => store.getters['inboxes/getUIFlags'].isCreating
);

const createChannel = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  try {
    const inbox = await store.dispatch('inboxes/createChannel', {
      name: form.channelName.trim(),
      channel: {
        type: 'api',
        webhook_url: form.webhookUrl.trim(),
        additional_attributes: {
          integration: 'worktool',
          robot_id: form.robotId.trim(),
        },
      },
    });

    router.replace({
      name: 'settings_inboxes_add_agents',
      params: { page: 'new', inbox_id: inbox.id },
    });
  } catch (error) {
    useAlert(
      error.message || t('INBOX_MGMT.ADD.WORKTOOL_CHANNEL.API.ERROR_MESSAGE')
    );
  }
};
</script>

<template>
  <div class="h-full w-full p-6 col-span-6">
    <PageHeader
      :header-title="$t('INBOX_MGMT.ADD.WORKTOOL_CHANNEL.TITLE')"
      :header-content="$t('INBOX_MGMT.ADD.WORKTOOL_CHANNEL.DESC')"
    />

    <form class="flex flex-col mx-0" @submit.prevent="createChannel">
      <div class="flex-shrink-0 flex-grow-0">
        <label :class="{ error: v$.channelName.$error }">
          {{ $t('INBOX_MGMT.ADD.WORKTOOL_CHANNEL.CHANNEL_NAME.LABEL') }}
          <input
            v-model="form.channelName"
            type="text"
            :placeholder="
              $t('INBOX_MGMT.ADD.WORKTOOL_CHANNEL.CHANNEL_NAME.PLACEHOLDER')
            "
            @blur="v$.channelName.$touch"
          />
          <span v-if="v$.channelName.$error" class="message">
            {{ $t('INBOX_MGMT.ADD.WORKTOOL_CHANNEL.CHANNEL_NAME.ERROR') }}
          </span>
        </label>
      </div>

      <div class="flex-shrink-0 flex-grow-0">
        <label :class="{ error: v$.robotId.$error }">
          {{ $t('INBOX_MGMT.ADD.WORKTOOL_CHANNEL.ROBOT_ID.LABEL') }}
          <input
            v-model="form.robotId"
            type="text"
            :placeholder="
              $t('INBOX_MGMT.ADD.WORKTOOL_CHANNEL.ROBOT_ID.PLACEHOLDER')
            "
            @blur="v$.robotId.$touch"
          />
          <span v-if="v$.robotId.$error" class="message">
            {{ $t('INBOX_MGMT.ADD.WORKTOOL_CHANNEL.ROBOT_ID.ERROR') }}
          </span>
        </label>
      </div>

      <div class="flex-shrink-0 flex-grow-0">
        <label :class="{ error: v$.webhookUrl.$error }">
          {{ $t('INBOX_MGMT.ADD.WORKTOOL_CHANNEL.WEBHOOK_URL.LABEL') }}
          <input
            v-model="form.webhookUrl"
            type="url"
            :placeholder="
              $t('INBOX_MGMT.ADD.WORKTOOL_CHANNEL.WEBHOOK_URL.PLACEHOLDER')
            "
            @blur="v$.webhookUrl.$touch"
          />
          <span v-if="v$.webhookUrl.$error" class="message">
            {{ v$.webhookUrl.$errors[0].$message }}
          </span>
        </label>
        <p class="help-text">
          {{ $t('INBOX_MGMT.ADD.WORKTOOL_CHANNEL.WEBHOOK_URL.SUBTITLE') }}
        </p>
      </div>

      <div class="w-full mt-4">
        <NextButton
          :is-loading="isCreating"
          type="submit"
          solid
          blue
          :label="$t('INBOX_MGMT.ADD.WORKTOOL_CHANNEL.SUBMIT_BUTTON')"
        />
      </div>
    </form>
  </div>
</template>
